import { Box, SxProps, Theme } from '@mui/material';
import { useDialogStore } from 'src/stores/dialog';
import CloseIconButton, { CloseIconButtonProps } from './CloseIconButton';

export const UNSAVED_CHANGES_TITLE = 'Unsaved changes';
export const UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes. Are you sure you want to close this window?';

/**
 * Returns a guarded close handler. When `hasUnsavedChanges` is true it shows the
 * in-app confirm dialog (not the browser-native window.confirm) before closing.
 * Use this for ALL close paths (X button, Cancel button, Dialog onClose / ESC / backdrop)
 * so they behave consistently.
 */
export function useUnsavedCloseGuard(
  onClose?: (() => void) | null,
  hasUnsavedChanges?: boolean
) {
  const { confirmDialog } = useDialogStore();

  const requestClose = async () => {
    if (!onClose) return;
    if (hasUnsavedChanges) {
      const confirmed = await confirmDialog({
        title: UNSAVED_CHANGES_TITLE,
        description: UNSAVED_CHANGES_MESSAGE,
        confirmText: 'Yes',
        denyText: 'No',
      });
      if (!confirmed) return;
    }
    onClose();
  };

  return requestClose;
}

export interface DialogCloseButtonProps extends Omit<CloseIconButtonProps, 'onClick'> {
  /** Called to actually close the dialog (handleClose / onCancel / () => setOpen(false)). */
  onClose: () => void;
  /** When true, an in-app confirm guard runs before onClose. */
  hasUnsavedChanges?: boolean;
  disabled?: boolean;
  /** Pass-through styles merged onto the absolutely-positioned wrapper Box. */
  wrapperSx?: SxProps<Theme>;
}

/**
 * Absolutely positioned top-right Close (X) for bare MUI <Dialog>/<Modal>.
 * Render as the FIRST child of <Dialog> (before <DialogTitle>).
 */
const DialogCloseButton = ({
  onClose,
  hasUnsavedChanges = false,
  disabled = false,
  wrapperSx,
  ...iconButtonProps
}: DialogCloseButtonProps) => {
  const requestClose = useUnsavedCloseGuard(onClose, hasUnsavedChanges);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: (theme) => theme.zIndex.modal + 1,
        ...wrapperSx,
      }}
    >
      <CloseIconButton
        onClick={() => {
          void requestClose();
        }}
        disabled={disabled}
        data-name="dialog-close-button"
        {...iconButtonProps}
      />
    </Box>
  );
};

export default DialogCloseButton;
